import { createGeminiImage, isGeminiAvailable } from './gemini-client.js';

const form = document.querySelector('form[data-avatar-form]');
const breedSelect = document.querySelector('#avatar-breed');
const accentSelect = document.querySelector('#avatar-accent');
const notesInput = document.querySelector('#avatar-notes');
const statusEl = document.querySelector('[data-status]');
const previewFrame = document.querySelector('[data-avatar-preview]');
const downloadBtn = document.querySelector('[data-download]');
const resetBtn = document.querySelector('[data-reset]');
const submitBtn = form?.querySelector('button[type="submit"]');

const DEFAULT_STATUS = 'Pick a breed and mood, then tap generate.';
let geminiReady = true;

const BREED_PROMPTS = {
  netherland:
    'Feature a Netherland Dwarf rabbit headshot with a petite snout, rounded cheeks, and upright polished ears.',
  holland: 'Draw a Holland Lop rabbit with plush fur, gentle cheeks, and signature floppy ears framing the face.',
  lionhead: 'Showcase a Lionhead rabbit with a fluffy mane framing bright eyes, a tiny nose, and tidy whiskers.',
  dutch: 'Illustrate a Dutch rabbit face with crisp symmetrical blaze markings that split the face color.',
  flemish: 'Depict a Flemish Giant rabbit with an elongated face, velvety fur texture, and relaxed expression.'
};

const ACCENT_PROMPTS = {
  pastel: 'Color the avatar with patisserie-inspired pastels, macaron pinks, and buttercream yellows.',
  nocturne: 'Wrap the avatar in cinematic midnight lighting with sapphire gradients and soft rim lights.',
  garden: 'Frame the portrait with botanical leaves, delicate blossoms, and mossy green watercolor washes.',
  festival: 'Add ribbon streamers, confetti sparks, and warm festival lighting behind the rabbit.'
};

function buildPrompt(breedKey, accentKey, notes = '') {
  const breedPrompt = BREED_PROMPTS[breedKey] || BREED_PROMPTS.netherland;
  const accentPrompt = ACCENT_PROMPTS[accentKey] || ACCENT_PROMPTS.pastel;
  const extraNotes = notes.trim();
  return `Illustrate a Croissant Bunny cartoon headshot, crisp linework, square canvas, transparent friendly background. ${breedPrompt} ${accentPrompt} Emphasize glossy eyes, subtle shading, and sticker-ready finish.${extraNotes ? ` Additional direction: ${extraNotes}.` : ''}`;
}

function setStatus(message, isError = false) {
  if (!statusEl) return;
  statusEl.textContent = message;
  statusEl.classList.toggle('error', Boolean(isError));
}

function setPreview(base64) {
  if (!previewFrame) return;
  if (!base64) {
    previewFrame.style.backgroundImage = '';
    previewFrame.classList.add('placeholder');
    downloadBtn?.classList.add('is-hidden');
    if (downloadBtn) {
      downloadBtn.href = 'javascript:void(0)';
    }
    downloadBtn?.setAttribute('aria-disabled', 'true');
    downloadBtn?.setAttribute('tabindex', '-1');
    return;
  }

  const dataUrl = `data:image/png;base64,${base64}`;
  previewFrame.style.backgroundImage = `url(${dataUrl})`;
  previewFrame.classList.remove('placeholder');
  if (downloadBtn) {
    downloadBtn.href = dataUrl;
    downloadBtn.classList.remove('is-hidden');
    downloadBtn.removeAttribute('aria-disabled');
    downloadBtn.removeAttribute('tabindex');
  }
}

async function initGeminiAvailability() {
  if (!form || !statusEl) return;
  geminiReady = await isGeminiAvailable();
  if (!geminiReady) {
    submitBtn?.setAttribute('disabled', 'true');
    form.classList.add('tool-disabled');
    setStatus('Image generation is offline. Please try again later.');
  }
}

function handleReset() {
  if (!form) return;
  form.reset();
  setPreview('');
  setStatus(DEFAULT_STATUS);
}

if (form && breedSelect && accentSelect && previewFrame && statusEl) {
  handleReset();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!geminiReady) {
      setStatus('Image generation is offline. Please try later.', true);
      return;
    }

    const breed = breedSelect.value;
    const accent = accentSelect.value;
    const notes = notesInput?.value || '';
    const prompt = buildPrompt(breed, accent, notes);

    try {
      setStatus('Painting your avatar—this usually takes 10-15 seconds…');
      setPreview('');
      const output = await createGeminiImage({ prompt });
      setPreview(output);
      setStatus('Avatar ready! Download the PNG or adjust the inputs.');
    } catch (error) {
      console.error(error);
      setStatus(error.message || 'Generation failed. Please try again.', true);
      setPreview('');
    }
  });

  resetBtn?.addEventListener('click', handleReset);
  initGeminiAvailability();
}
